import { useEffect, useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { confirmaNome } from '@malgoos/shared';
import { Botao } from './base';
import { Entrada, Rotulo, Selecao } from './formulario';
import { Modal } from './sobreposicao';

/**
 * Item 5 (21/10): confirmação forte das exclusões de Plataforma (tenant), Workspace e Empresa. Excluir é arquivar (nada é
 * apagado); com `destinos`, a pessoa escolhe antes se os dados ficam arquivados junto ou passam para outro destino. O botão
 * só libera com o nome digitado (a mesma regra da API, `confirmaNome`). Mesmo Modal, campos e radios das outras telas.
 */
export function ConfirmarExclusao({
  aberto,
  aoFechar,
  aoConfirmar,
  titulo,
  texto,
  nome,
  outrosNomes = [],
  destinos,
  carregando,
}: {
  aberto: boolean;
  aoFechar: () => void;
  aoConfirmar: (p: { confirmacao: string; destinoId: string | null }) => void;
  titulo: string;
  texto: ReactNode;
  /** O nome que aparece para digitar. */
  nome: string;
  /** Outros nomes que também valem (ex.: a razão social além do nome fantasia). */
  outrosNomes?: (string | null | undefined)[];
  /** Para onde os dados podem ir; sem a lista (ou vazia), só arquivar. */
  destinos?: { valor: string; rotulo: string }[];
  carregando?: boolean;
}) {
  const { t } = useTranslation();
  const id = useId();
  const [digitado, setDigitado] = useState('');
  const [transferir, setTransferir] = useState(false);
  const [destino, setDestino] = useState('');
  useEffect(() => {
    if (!aberto) {
      setDigitado('');
      setTransferir(false);
      setDestino('');
    }
  }, [aberto]);
  const temDestinos = !!destinos?.length;
  const pronto = confirmaNome(digitado, nome, ...outrosNomes) && (!transferir || !!destino);
  const confirmar = () => pronto && aoConfirmar({ confirmacao: digitado, destinoId: transferir ? destino : null });
  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={titulo}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {t('comum.cancelar')}
          </Botao>
          <Botao variante="perigo" carregando={carregando} disabled={!pronto} onClick={confirmar}>
            {t('exclusao.estrutura.excluir')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4 text-sm text-tinta-2">
        <div>{texto}</div>
        {temDestinos && (
          <fieldset>
            <legend className="mb-3 text-sm font-medium text-tinta-2">{t('exclusao.estrutura.oQueFazer')}</legend>
            <ul className="divide-y divide-borda rounded-lg border border-borda bg-sup-2">
              {([false, true] as const).map((x) => (
                <li key={String(x)}>
                  <label className="flex cursor-pointer items-start gap-3 px-3 py-2.5 text-sm">
                    <input type="radio" name={`${id}-destino`} checked={transferir === x} onChange={() => setTransferir(x)} className="mt-0.5 size-4 accent-acento" />
                    <span className="flex min-w-0 flex-col">
                      <span className="font-medium text-tinta-1">{t(x ? 'exclusao.estrutura.transferir' : 'exclusao.estrutura.arquivar')}</span>
                      <span className="text-xs text-tinta-3">{t(x ? 'exclusao.estrutura.transferirAjuda' : 'exclusao.estrutura.arquivarAjuda')}</span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
        )}
        {temDestinos && transferir && (
          <Rotulo id={`${id}-alvo`} rotulo={t('exclusao.estrutura.destino')} obrigatorio>
            <Selecao id={`${id}-alvo`} value={destino} onChange={(e) => setDestino(e.target.value)} opcoes={destinos!} vazio="" />
          </Rotulo>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            confirmar();
          }}
        >
          <Rotulo id={`${id}-nome`} rotulo={t('exclusao.estrutura.digite', { nome })}>
            <Entrada id={`${id}-nome`} value={digitado} onChange={(e) => setDigitado(e.target.value)} autoComplete="off" spellCheck={false} data-confirmacao-nome="" />
          </Rotulo>
        </form>
      </div>
    </Modal>
  );
}
